import React, { useMemo } from 'react';
import { Paper, Stack, Button, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../common/SearchableSelect';
import { useTranslation } from 'react-i18next';

/**
 * Filter bar for the Assigned -> Before-Challan dashboard.
 * `view` is 'pending' or 'duration' — controls which extra controls show.
 */
const AssignedToChallanFilter = ({
    view,
    supervisors = [],
    supervisorsLoading = false,
    selectedSupervisorId,
    onSupervisorChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    unitType,
    onUnitTypeChange,
    groupBy,
    onGroupByChange,
    onSubmit,
    loading,
    disableSupervisorSelector = false,
}) => {
    const { t } = useTranslation();
    const supervisorOptions = useMemo(() =>
        supervisors.map((s) => ({
            value: s.id,
            label: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || s.email || String(s.id),
        })),
        [supervisors]
    );

    const showUnitType = view === 'pending';
    const showGroupBy = view === 'duration';

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3, mb: 3, borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.08)',
                bgcolor: '#FFFFFF',
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
                {!disableSupervisorSelector && (
                    <div style={{ minWidth: 220 }}>
                        <SearchableSelect
                            options={supervisorOptions}
                            value={selectedSupervisorId}
                            onChange={(e) => onSupervisorChange(e.target.value)}
                            placeholder={t('common.formFields.placeholders.allSupervisors')}
                            disabled={supervisorsLoading}
                            loading={supervisorsLoading}
                        />
                    </div>
                )}

                <TextField
                    label={t('common.filters.from')}
                    type="date"
                    size="small"
                    value={fromDate}
                    onChange={(e) => onFromDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 160 }}
                />
                <TextField
                    label={t('common.filters.to')}
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={(e) => onToDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 160 }}
                />

                {showUnitType && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>{t('assignedToChallan.filters.unitType')}</InputLabel>
                        <Select label={t('assignedToChallan.filters.unitType')} value={unitType} onChange={(e) => onUnitTypeChange(e.target.value)}>
                            <MenuItem value="">{t('assignedToChallan.filters.options.all')}</MenuItem>
                            <MenuItem value="kg">{t('assignedToChallan.filters.options.kg')}</MenuItem>
                            <MenuItem value="mtr">{t('assignedToChallan.filters.options.meter')}</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {showGroupBy && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>{t('assignedToChallan.filters.groupBy')}</InputLabel>
                        <Select label={t('assignedToChallan.filters.groupBy')} value={groupBy} onChange={(e) => onGroupByChange(e.target.value)}>
                            <MenuItem value="none">{t('assignedToChallan.filters.options.none')}</MenuItem>
                            <MenuItem value="day">{t('assignedToChallan.filters.options.day')}</MenuItem>
                            <MenuItem value="supervisor">{t('assignedToChallan.filters.options.supervisor')}</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={loading}
                    startIcon={<FontAwesomeIcon icon={faSearch} />}
                    sx={{ bgcolor: '#189AB4', '&:hover': { bgcolor: '#147a8f' }, textTransform: 'none', fontWeight: 700, px: 3 }}
                >
                    {t('common.filters.apply')}
                </Button>
            </Stack>
        </Paper>
    );
};

export default AssignedToChallanFilter;
